import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const RelatedProducts = ({ currentProductId }) => {
  const relatedProducts = [
    {
      id: 2,
      name: "Organic Bananas",
      brand: "Fresh Farms",
      price: 2.99,
      originalPrice: 3.49,
      unit: "1 lb",
      image: "https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=300&h=300&fit=crop",
      rating: 4.6,
      reviewCount: 89,
      inStock: true,
      discount: 14
    },
    {
      id: 3,
      name: "Fresh Strawberries",
      brand: "Berry Best",
      price: 4.99,
      unit: "1 pint",
      image: "https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=300&h=300&fit=crop",
      rating: 4.8,
      reviewCount: 156,
      inStock: true
    },
    {
      id: 4,
      name: "Organic Oranges",
      brand: "Citrus Grove",
      price: 5.99,
      originalPrice: 6.99,
      unit: "2 lbs",
      image: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=300&h=300&fit=crop",
      rating: 4.4,
      reviewCount: 67,
      inStock: true,
      discount: 14
    },
    {
      id: 5,
      name: "Fresh Blueberries",
      brand: "Nature\'s Best",
      price: 6.99,
      unit: "1 pint",
      image: "https://images.unsplash.com/photo-1498557850523-fd3d118b962e?w=300&h=300&fit=crop",
      rating: 4.7,
      reviewCount: 123,
      inStock: false
    },
    {
      id: 6,
      name: "Organic Grapes",
      brand: "Vineyard Fresh",
      price: 7.99,
      unit: "2 lbs",
      image: "https://images.unsplash.com/photo-1537640538966-79f369143f8f?w=300&h=300&fit=crop",
      rating: 4.5,
      reviewCount: 94,
      inStock: true
    }
  ];

  const filteredProducts = relatedProducts.filter(product => product.id !== currentProductId);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={12} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="StarHalf" size={12} className="text-warning fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={12} className="text-border" />
      );
    }

    return stars;
  };

  const addToCart = (product) => {
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = existingCart.find(item => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      existingCart.push({ ...product, quantity: 1 });
    }
    
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    // Dispatch custom event to update cart count
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  };

  return (
    <div className="bg-surface rounded-card shadow-card">
      <div className="p-6 border-b border-border">
        <h3 className="font-heading font-heading-semibold text-xl text-text-primary">
          Related Products
        </h3>
        <p className="text-text-secondary font-body mt-1">
          Customers who viewed this item also viewed
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.slice(0, 4).map((product) => (
            <div key={product.id} className="bg-background rounded-card p-4 hover:shadow-card transition-smooth">
              {/* Product Image */}
              <div className="relative mb-4">
                <Link to={`/product-detail-page?id=${product.id}`}>
                  <div className="aspect-square bg-surface rounded-input overflow-hidden">
                    <Image
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
                
                {/* Discount Badge */}
                {product.discount && (
                  <div className="absolute top-2 left-2 bg-accent text-accent-foreground px-2 py-1 rounded-input text-xs font-data font-data-medium">
                    {product.discount}% OFF
                  </div>
                )}

                {/* Stock Status */}
                {!product.inStock && (
                  <div className="absolute inset-0 bg-surface/80 flex items-center justify-center rounded-input">
                    <span className="bg-error text-error-foreground px-3 py-1 rounded-input text-sm font-data font-data-medium">
                      Out of Stock
                    </span>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-2">
                <div>
                  <Link to={`/product-detail-page?id=${product.id}`}>
                    <h4 className="font-body-semibold text-text-primary hover:text-primary transition-smooth line-clamp-2">
                      {product.name}
                    </h4>
                  </Link>
                  <p className="text-sm text-text-secondary font-body">{product.brand}</p>
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-0.5">
                    {renderStars(product.rating)}
                  </div>
                  <span className="text-xs text-text-secondary font-data">
                    ({product.reviewCount})
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-center space-x-2">
                  <span className="font-data font-data-medium text-primary">
                    ${product.price.toFixed(2)}
                  </span>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <span className="text-sm text-text-secondary line-through font-data">
                      ${product.originalPrice.toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-text-secondary font-body">{product.unit}</p>

                {/* Add to Cart Button */}
                <Button
                  variant={product.inStock ? "primary" : "outline"}
                  onClick={() => product.inStock && addToCart(product)}
                  disabled={!product.inStock}
                  className="w-full mt-3"
                  iconName="ShoppingCart"
                  iconPosition="left"
                  size="sm"
                >
                  {product.inStock ? "Add to Cart" : "Out of Stock"}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* View More Button */}
        <div className="text-center mt-6">
          <Link to="/product-catalog-category-browse?category=fruits">
            <Button variant="outline">
              View More Products
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RelatedProducts;